import { useEffect } from "react";

/**
 * SEO helper — dynamically sets document title, meta description, canonical,
 * Open Graph and Twitter card tags, and (optionally) injects a JSON-LD block.
 * SPA-friendly. Cleans up on unmount so pages don't leak metadata into each
 * other. Safe fallbacks for missing values.
 *
 * Usage:
 *   useDocumentMeta({
 *     title: "Perth Comedy Club",
 *     description: "…",
 *     image: "https://.../cover.jpg",
 *     url: "https://…/e/perth-comedy",
 *     type: "event",         // OG type
 *     jsonLd: { "@context": "https://schema.org", "@type": "Event", … },
 *   });
 */
export function useDocumentMeta({ title, description, image, url, type = "website", jsonLd } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const ensure = (selector, create) => {
      let el = document.head.querySelector(selector);
      let created = false;
      if (!el) {
        el = create();
        document.head.appendChild(el);
        created = true;
      }
      return { el, created };
    };

    const setMeta = (name, value, attr = "name") => {
      if (!value) return null;
      const sel = `meta[${attr}="${name}"]`;
      const { el, created } = ensure(sel, () => {
        const m = document.createElement("meta");
        m.setAttribute(attr, name);
        return m;
      });
      const prev = el.getAttribute("content");
      el.setAttribute("content", value);
      return { el, prev, created };
    };

    const setLink = (rel, href) => {
      if (!href) return null;
      const sel = `link[rel="${rel}"]`;
      const { el, created } = ensure(sel, () => {
        const l = document.createElement("link");
        l.setAttribute("rel", rel);
        return l;
      });
      const prev = el.getAttribute("href");
      el.setAttribute("href", href);
      return { el, prev, created };
    };

    const changes = [
      setMeta("description", description),
      setMeta("og:title", title, "property"),
      setMeta("og:description", description, "property"),
      setMeta("og:type", type, "property"),
      setMeta("og:image", image, "property"),
      setMeta("og:url", url, "property"),
      setMeta("twitter:card", image ? "summary_large_image" : "summary"),
      setMeta("twitter:title", title),
      setMeta("twitter:description", description),
      setMeta("twitter:image", image),
      setLink("canonical", url),
    ].filter(Boolean);

    // JSON-LD block
    let ldEl = null;
    if (jsonLd) {
      ldEl = document.createElement("script");
      ldEl.type = "application/ld+json";
      ldEl.dataset.gtSeo = "1";
      ldEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ldEl);
    }

    return () => {
      document.title = prevTitle;
      for (const change of changes) {
        if (!change) continue;
        if (change.created) {
          change.el.remove();
        } else if (change.prev != null) {
          if (change.el.tagName === "LINK") change.el.setAttribute("href", change.prev);
          else change.el.setAttribute("content", change.prev);
        }
      }
      if (ldEl) ldEl.remove();
    };
  }, [title, description, image, url, type, jsonLd ? JSON.stringify(jsonLd) : null]);
}
